import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Badge, EmptyState, Skeleton } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { fetchProductImageObjectUrl, useProductImagesQuery } from '../api';
import type { ProductImage } from '../types';

import { useSession } from '@/lib/session';

type ResolvedState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly objectUrl: string }
  | { readonly status: 'error' };

/**
 * Images Produits lot — the merchant's REAL gallery: every image already
 * persisted for this product, re-fetched on every mount (page load,
 * navigation back, refresh) via `GET /merchant/products/:id/images`, each
 * thumbnail then individually resolved through the authenticated raw
 * endpoint (`fetchProductImageObjectUrl`) since `<img src>` cannot carry
 * the `Authorization` header that route requires.
 *
 * Replaces the previous, honestly-labelled limitation ("aperçu de cette
 * session uniquement") — this now IS the real, cross-session gallery.
 */
export function ProductImageGallery({ productId }: { productId: string }): ReactElement {
  const { t } = useI18n();
  const { accessToken, activeShopId } = useSession();
  const imagesQuery = useProductImagesQuery(productId);
  const [resolved, setResolved] = useState<Record<string, ResolvedState>>({});

  // Tracks the currently-live object URLs so the cleanup effect below can
  // revoke exactly the ones THIS component created, without depending on
  // `resolved` itself (which would re-run the effect, and re-revoke,
  // every time a single image finishes resolving).
  const objectUrlsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (accessToken === null || activeShopId === null) return;
    const images = imagesQuery.data;
    if (images === undefined) return;

    let cancelled = false;

    for (const image of images) {
      if (objectUrlsRef.current[image.id] !== undefined) continue;

      setResolved((current) => ({ ...current, [image.id]: { status: 'loading' } }));

      void fetchProductImageObjectUrl(productId, image.id, accessToken, activeShopId)
        .then((objectUrl) => {
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          objectUrlsRef.current[image.id] = objectUrl;
          setResolved((current) => ({ ...current, [image.id]: { status: 'ready', objectUrl } }));
        })
        .catch(() => {
          if (!cancelled) {
            setResolved((current) => ({ ...current, [image.id]: { status: 'error' } }));
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [accessToken, activeShopId, productId, imagesQuery.data]);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
    };
  }, []);

  if (imagesQuery.isPending) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((key) => (
          <div key={key} className="aspect-square w-full overflow-hidden rounded-lg">
            <Skeleton shape="block" width="100%" height="100%" labelled={key === 0} />
          </div>
        ))}
      </div>
    );
  }

  if (imagesQuery.isError) {
    return (
      <EmptyState
        title={t('merchant.products.images.loadError')}
        description={t(resolveErrorMessageKey(imagesQuery.error))}
      />
    );
  }

  const images = imagesQuery.data;
  if (images.length === 0) {
    return <p className="text-sm text-inkFaint">{t('merchant.products.images.empty')}</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {images.map((image: ProductImage) => {
        const state = resolved[image.id];
        return (
          <div key={image.id} className="overflow-hidden rounded-lg border border-line">
            {state?.status === 'ready' ? (
              <img
                src={state.objectUrl}
                alt={image.altText ?? ''}
                className="aspect-square w-full object-cover"
              />
            ) : state?.status === 'error' ? (
              <div className="flex aspect-square w-full items-center justify-center bg-surface2">
                <Badge tone="critical">{t('merchant.products.images.loadErrorBadge')}</Badge>
              </div>
            ) : (
              <div className="aspect-square w-full">
                <Skeleton shape="block" width="100%" height="100%" />
              </div>
            )}
            <div className="p-2">
              <p className="truncate text-xs text-inkFaint">
                {image.altText ?? t('merchant.products.images.position', { n: image.sortOrder })}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
